import { ClientSideRateLimit, SecurityMonitor } from '../../utils/security';

/**
 * @file Manages the instantiation and configuration of security-related services.
 * This includes rate limiters for API and general application usage.
 */

/**
 * Rate limiter for critical API endpoints.
 * @description Limits requests to 20 per minute to prevent abuse.
 */
export const apiRateLimit = new ClientSideRateLimit({
  max: 20,
  windowMs: 60 * 1000,
});

/**
 * Rate limiter for general application actions.
 * @description Limits requests to 50 per minute to mitigate spam and excessive usage.
 */
export const generalRateLimit = new ClientSideRateLimit({
  max: 50,
  windowMs: 60 * 1000,
});

/**
 * Wraps the rate-limiting check with security event logging.
 * @param {ClientSideRateLimit} limiter - The rate limiter instance to use.
 * @param {string} key - A unique identifier for the action being rate-limited.
 * @param {string} eventType - The event type to log if the action is limited.
 * @returns {boolean} - True if the action is rate-limited, false otherwise.
 */
export const isRateLimited = (
  limiter: ClientSideRateLimit,
  key: string,
  eventType: 'API_RATE_LIMITED' | 'GENERAL_RATE_LIMITED'
): boolean => {
  const limited = !limiter.isAllowed(key);
  if (limited) {
    SecurityMonitor.logSecurityEvent(eventType, { key });
  }
  return limited;
};
